import { firebase } from "../config/configureFirebase";
import { FareCalculator } from "../other/FareCalculator";
import { GetDistance, GetTripDistance } from "../other/GeoFunctions";
import { fetchAddressfromCoords } from '../other/GoogleAPIFunctions';
import { isFlatRateDriver } from './flatRate';
import store from '../store/store';
import { query, get, orderByKey } from "firebase/database";

/**
 * Fetches fresh GPS location and address for booking confirmation
 * @param {Object} currentGpsLocation - Current GPS location from Redux (gps.location)
 * @param {Object} estimatePickup - Pickup location from estimate data
 * @param {boolean} isManuallySet - Whether pickup was manually set by user
 * @param {Function} getAddressFromCache - Function to get cached address
 * @param {Function} saveAddressToCache - Function to save address to cache
 * @param {string} logPrefix - Prefix for console logs (e.g., '[BOOKING]', '[DRIVER BOOKING]')
 * @returns {Object} Fresh pickup location with lat, lng, add, and source
 */
export const getFreshPickupLocation = async (
    currentGpsLocation,
    estimatePickup,
    isManuallySet,
    getAddressFromCache,
    saveAddressToCache,
    logPrefix = '[BOOKING]'
) => {
    // ALWAYS prefer the freshest GPS reading we can find. The caller passes
    // `currentGpsLocation` from a React closure, which can be many seconds
    // (and many meters) stale by the time we run — especially with the
    // emulator route simulator at 4×. Fall through to store.getState() so
    // the booking payload's pickup matches where the user actually is RIGHT
    // NOW, not where they were when the screen last rendered.
    try {
        const storeGps = store.getState && store.getState()?.gpsdata?.location;
        if (storeGps?.lat != null && storeGps?.lng != null) {
            // If we got a store-side GPS that's different from the closure
            // value, use the store's. They'll match in the common case.
            currentGpsLocation = storeGps;
        }
    } catch (_) { /* defensive — store may not be available in some test contexts */ }

    // Validate estimatePickup has required fields
    // Handle both formats: { lat, lng, add } and { coords: { lat, lng }, description }
    let defaultPickup;
    if (estimatePickup?.coords?.lat !== undefined && estimatePickup?.coords?.lng !== undefined) {
        // Old format - convert to flat format
        defaultPickup = {
            lat: estimatePickup.coords.lat,
            lng: estimatePickup.coords.lng,
            add: estimatePickup.description || estimatePickup.add || '',
            source: estimatePickup.source || 'estimate'
        };
    } else if (estimatePickup?.lat !== undefined && estimatePickup?.lng !== undefined) {
        // New flat format
        defaultPickup = {
            lat: estimatePickup.lat,
            lng: estimatePickup.lng,
            add: estimatePickup.add || estimatePickup.description || '',
            source: estimatePickup.source || 'estimate'
        };
    } else {
        console.error(`${logPrefix} Invalid estimatePickup:`, JSON.stringify(estimatePickup));
        throw new Error('Invalid estimatePickup - missing coordinates');
    }

    // Use validated estimate pickup as default
    let freshPickupLocation = defaultPickup;

    // Helper: does this string look like raw GPS coords ("9.000056, 38.785555")?
    // Used so we never let a coordinate-string masquerade as a "real address"
    // in the booking payload.
    const looksLikeCoords = (s) =>
        typeof s === 'string' && /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(s.trim());

    // Helper: meters between two lat/lng points (cheap equirectangular).
    const metersBetween = (a, b) => {
        if (!a || !b) return Infinity;
        const dLat = (a.lat - b.lat) * 111000;
        const dLng = (a.lng - b.lng) * 111000 * Math.cos((a.lat * Math.PI) / 180);
        return Math.sqrt(dLat * dLat + dLng * dLng);
    };

    // Check if we should use fresh GPS location
    if (!isManuallySet && currentGpsLocation?.lat && currentGpsLocation?.lng) {
        // Fast path: if the user hasn't moved more than ~30 m AND the estimate
        // already carries a real address (the one TaxiModal showed), reuse it.
        // Saves a Geocoding API call AND avoids the bug below where a failed
        // fetch would write GPS coordinates as the booking address.
        const moved = metersBetween(
            { lat: currentGpsLocation.lat, lng: currentGpsLocation.lng },
            { lat: defaultPickup.lat, lng: defaultPickup.lng }
        );
        if (moved < 30 && defaultPickup.add && !looksLikeCoords(defaultPickup.add)) {
            return {
                lat: currentGpsLocation.lat,
                lng: currentGpsLocation.lng,
                add: defaultPickup.add,
                source: 'gps_fresh',
            };
        }

        // Slow path: user moved or we don't have a clean address to reuse.
        // Try cache, then a real geocoding call.
        let freshAddress = null;
        try {
            const cachedAddress = getAddressFromCache(currentGpsLocation.lat, currentGpsLocation.lng);
            if (cachedAddress && !looksLikeCoords(cachedAddress)) {
                freshAddress = cachedAddress;
            } else {
                // BUG-FIX: fetchAddressfromCoords expects ONE comma-separated
                // "lat,lng" string (see GoogleAPIFunctions.js line 258), not
                // two number arguments. The previous call passed two numbers,
                // so the first number became `latlng`, the URL was malformed,
                // the call threw, and the catch block wrote raw coordinates
                // as the booking address — which is what GoogleDriverTrips
                // was displaying as "9.0000556, 38.785555".
                const latlng = `${currentGpsLocation.lat},${currentGpsLocation.lng}`;
                const addressResult = await fetchAddressfromCoords(latlng);
                if (
                    addressResult &&
                    addressResult !== 'No results found' &&
                    !looksLikeCoords(addressResult)
                ) {
                    freshAddress = addressResult;
                    try {
                        await saveAddressToCache(
                            currentGpsLocation.lat,
                            currentGpsLocation.lng,
                            addressResult
                        );
                    } catch (_) { /* cache save failure is non-fatal */ }
                }
            }
        } catch (_) {
            // Geocode failed — keep freshAddress null so we fall back to the
            // estimate's address (which TaxiModal showed) below, NOT to coords.
        }

        // Pick the best address we have, in priority order:
        //   1. Freshly fetched address from the GPS coords
        //   2. The estimate's address, if it's a real address (not coords)
        //   3. Last resort: coordinate string (only when we have nothing else)
        const fallbackAddress = freshAddress
            || (defaultPickup.add && !looksLikeCoords(defaultPickup.add) ? defaultPickup.add : null)
            || `${currentGpsLocation.lat.toFixed(6)}, ${currentGpsLocation.lng.toFixed(6)}`;

        freshPickupLocation = {
            lat: currentGpsLocation.lat,
            lng: currentGpsLocation.lng,
            add: fallbackAddress,
            source: 'gps_fresh',
        };
    }

    return freshPickupLocation;
};

export const formatBookingObject = async (bookingData, settings) => {
  const c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const reference = [...Array(6)].map(_ => c[~~(Math.random()*c.length)]).join('');
  const { config } = firebase;
  let today;
  try{
      let res =  await fetch(`https://${config.projectId}.web.app/getservertime`, { method: 'GET', headers: {'Content-Type': 'application/json'}});
      const json = await res.json();
      if(json.time){
        today = json.time;
      } else{
        today = new Date().getTime();
      }
  }catch (err){
    today = new Date().getTime();
  }

  // Handle both data structures: 
  // 1. Old format: { coords: { lat, lng }, description }
  // 2. New format from getFreshPickupLocation: { lat, lng, add }
  let pickUp;
  if (bookingData.pickup?.coords?.lat !== undefined && bookingData.pickup?.coords?.lng !== undefined) {
    // Old format with coords wrapper
    pickUp = { lat: bookingData.pickup.coords.lat, lng: bookingData.pickup.coords.lng, add: bookingData.pickup.description || bookingData.pickup.add || '' };
  } else if (bookingData.pickup?.lat !== undefined && bookingData.pickup?.lng !== undefined) {
    // New flat format from getFreshPickupLocation
    pickUp = { lat: bookingData.pickup.lat, lng: bookingData.pickup.lng, add: bookingData.pickup.add || bookingData.pickup.description || '' };
  } else {
    console.error('[formatBookingObject] Invalid pickup data structure:', JSON.stringify(bookingData.pickup));
    throw new Error('Invalid pickup location data - missing coordinates');
  }

  let drop;
  if (bookingData.drop?.coords?.lat !== undefined && bookingData.drop?.coords?.lng !== undefined) {
    // Old format with coords wrapper
    drop = { lat: bookingData.drop.coords.lat, lng: bookingData.drop.coords.lng, add: bookingData.drop.description || bookingData.drop.add || '' };
  } else if (bookingData.drop?.lat !== undefined && bookingData.drop?.lng !== undefined) {
    // New flat format
    drop = { lat: bookingData.drop.lat, lng: bookingData.drop.lng, add: bookingData.drop.add || bookingData.drop.description || '' };
  } else {
    console.error('[formatBookingObject] Invalid drop data structure:', JSON.stringify(bookingData.drop));
    throw new Error('Invalid drop location data - missing coordinates');
  }
  
  let coords = [{latitude: pickUp.lat,longitude: pickUp.lng}];
  if(bookingData.drop.waypointsStr){
      bookingData.drop.waypointsStr.split("|").forEach((point)=>{
          let lat = point.split(',')[0];
          let lng = point.split(',')[1];
          coords.push({latitude: parseFloat(lat), longitude: parseFloat(lng)});
      });
  }
  coords.push({latitude: drop.lat,longitude: drop.lng});
  
  var otp;
  if(bookingData.settings.otp_secure && bookingData.otp) {
      otp = bookingData.otp
  } else if (bookingData.settings.otp_secure && !bookingData.otp)
      otp = Math.floor(Math.random() * 90000) + 10000;
  else {
      otp = false;
  }
  
    // Shared promo + payable math used by both branches.
    //   discount      — number paid out as a discount (kept as a number here
    //                   so the math is straightforward; PaymentDetails / web
    //                   admin both accept a number or a stringified number)
    //   tripCostNum   — base fare BEFORE the discount (offerFare wins over
    //                   the estimate, same rule as before)
    //   payableAmount — what the customer actually owes after the discount.
    //                   Was previously never set, so PaymentDetails fell back
    //                   to `trip_cost` and the discount line never reduced
    //                   the total. We compute it here once for both branches.
    const promoApplied = !!bookingData.promo_applied;
    const promoDetails = bookingData.promo_details ? bookingData.promo_details : null;
    const discountNum = (() => {
        if (!promoApplied) return 0;
        const d = parseFloat(bookingData.discount);
        return Number.isFinite(d) && d > 0 ? d : 0;
    })();
    const tripCostNum = parseFloat(
        bookingData.offerFare > 0 ? bookingData.offerFare : bookingData.estimate.estimateFare
    );
    const payableAmount = parseFloat(
        Math.max(0, tripCostNum - discountNum).toFixed(settings.decimal)
    );

    if (bookingData.manualRequest) {
        let dt = new Date();
        let localString = dt.getHours() + ":" + dt.getMinutes() + ":" + dt.getSeconds();
        let timeString = dt.getTime();
        // Flat-daily driver keeps the whole fare — the day is already
        // bought. Predicate must mirror server settlement
        // (functions/common/flatRate.js).
        const flatRateDriver = isFlatRateDriver(bookingData.driver, settings, store.getState().cartypes.cars);
        return {
            carType: bookingData.carDetails.name,
            carImage: bookingData.carDetails.image,
            customer: bookingData.customerData.uid,
            commission_type: bookingData.carDetails.convenience_fee_type,
            commission_rate: bookingData.carDetails.convenience_fees,
            reference: reference,
            customer_email: bookingData.customerData.email,
            customer_name: bookingData.customerData.firstName + ' ' + bookingData.customerData.lastName,
            customer_contact: bookingData.customerData.mobile? bookingData.customerData.mobile: ' ',
            customer_token: bookingData.customerData.pushToken? bookingData.customerData.pushToken: ' ',
            customer_image: bookingData.customerData.profile_image ? bookingData.customerData.profile_image : "",
            drop: drop,
            pickup: pickUp,
            estimate: bookingData.estimate.estimateFare,
            estimateDistance: bookingData.estimate.estimateDistance,
            distance: bookingData.estimate.estimateDistance,
            estimateTime:bookingData.estimate.estimateTime,
            status: bookingData?.source === "Driver" ? "STARTED" : "ARRIVED",
            source: bookingData?.source ? bookingData?.source : "Customer",
            trip_start_time: bookingData?.source === "Driver" ? localString : null,
            startTime: bookingData?.source === "Driver" ? timeString : null,
            driver_arrive_time: bookingData?.source === "Driver" && bookingData.tripdate ? bookingData.tripdate.toString() : null,
            bookLater:bookingData.bookLater,
            tripdate: bookingData.bookLater?bookingData.tripdate:today,
            bookingDate: today,
            otp: false,
            booking_type_admin:bookingData.booking_type_admin,
            coords: coords,
            waypoints: bookingData.drop.waypoints? bookingData.drop.waypoints: null,
            tripInstructions: bookingData.tripInstructions? bookingData.tripInstructions: null,
            trip_cost: bookingData.offerFare > 0 ? bookingData.offerFare : (parseFloat(bookingData.estimate.estimateFare)).toFixed(settings.decimal),
            convenience_fees: bookingData.estimate.convenience_fees,
            driver_share: (parseFloat(bookingData.estimate.estimateFare) - parseFloat(bookingData.estimate.convenience_fees)).toFixed(settings.decimal),
            // Flat overlay AFTER the per-trip fields so the stamped values
            // win: zero commission, driver keeps the full trip_cost.
            ...(flatRateDriver ? {
                convenience_fees: 0,
                driver_share: bookingData.offerFare > 0 ? bookingData.offerFare : (parseFloat(bookingData.estimate.estimateFare)).toFixed(settings.decimal),
                flat_rate_driver: true,
            } : {}),
            fleet_admin_comission: bookingData.carDetails.fleet_admin_fee ? bookingData.carDetails.fleet_admin_fee : null,
            paymentPacket: bookingData.paymentPacket? bookingData.paymentPacket : null,
            driver: bookingData.driver.uid,
            driver_image: bookingData.driver.profile_image ? bookingData.driver.profile_image: null,
            car_image: bookingData.driver.car_image ? bookingData.driver.car_image : bookingData.carDetails.image,
            driver_name: bookingData.driver.firstName + " " + bookingData.driver.lastName,
            driver_contact: bookingData.driver.mobile,
            driver_token: bookingData.driver.pushToken,
            vehicle_number: bookingData.driver.vehicleNumber ? bookingData.driver.vehicleNumber : "",
            driverRating: bookingData.driver.rating ? bookingData.driver.rating : "0",
            fleetadmin : bookingData.driver.fleetadmin ? bookingData.driver.fleetadmin : "",
            driverDeviceId : bookingData.driver.driverDeviceId  ? bookingData.driver.driverDeviceId : null,
            payment_mode: bookingData.payment_mode,
            requestedDrivers: null,
            manualRequest: true,
            offerFare: bookingData.offerFare,
            streetPickup: bookingData.streetPickup ? bookingData.streetPickup : false,
            booking_from_web: bookingData.booking_from_web? bookingData.booking_from_web: false,
            // Promo persistence — manual-request branch was missing these,
            // so a driver-applied promo (RiderSearchScreen / DriverSearchScreen)
            // never made it into /bookings and PaymentDetails couldn't show
            // the discount line. Per-user reuse is allowed (no usersUsed write).
            promo_applied: promoApplied,
            promo_details: promoDetails,
            discount: discountNum,
            payableAmount: payableAmount,
            ...bookingData.instructionData
        }
    }
    else {
      return {
          carType: bookingData.carDetails.name,
          carImage: bookingData.carDetails.image,
          customer: bookingData.userDetails.uid,
          commission_type: bookingData.carDetails.convenience_fee_type,
          commission_rate: bookingData.carDetails.convenience_fees,
          reference: reference,
          customer_email: bookingData.userDetails.email,
          customer_name: bookingData.userDetails.firstName + ' ' + bookingData.userDetails.lastName,
          customer_contact: bookingData.userDetails.mobile? bookingData.userDetails.mobile: ' ',
          customer_token: bookingData.userDetails.pushToken? bookingData.userDetails.pushToken: ' ',
          customer_image: bookingData.userDetails.profile_image ? bookingData.userDetails.profile_image : "",
          drop: drop,
          pickup: pickUp,
          estimate: bookingData.estimate.estimateFare,
          offerFare: bookingData.offerFare,
          estimateDistance: bookingData.estimate.estimateDistance,
          distance: bookingData.estimate.estimateDistance,
          estimateTime:bookingData.estimate.estimateTime,
          status: bookingData.booking_type_admin || !settings.prepaid || bookingData.booking_type_fleetadmin ? "NEW" : "PAYMENT_PENDING",
          bookLater:bookingData.bookLater,
          tripdate: bookingData.bookLater?bookingData.tripdate:today,
          bookingDate: today,
          otp: otp,
          booking_type_admin:bookingData.booking_type_admin,
          coords: coords,
          waypoints: bookingData.drop.waypoints? bookingData.drop.waypoints: null,
          roundTrip: bookingData.roundTrip? bookingData.roundTrip:null,
          tripInstructions: bookingData.tripInstructions? bookingData.tripInstructions: null,
          trip_cost: bookingData.offerFare > 0 ? bookingData.offerFare : bookingData.estimate.estimateFare,
          convenience_fees: bookingData.estimate.convenience_fees,
          driver_share: (parseFloat(bookingData.estimate.estimateFare) - parseFloat(bookingData.estimate.convenience_fees)).toFixed(settings.decimal),
          fleet_admin_comission: bookingData.carDetails.fleet_admin_fee ? bookingData.carDetails.fleet_admin_fee : null,
          paymentPacket: bookingData.paymentPacket? bookingData.paymentPacket : null,
          preRequestedDrivers: bookingData.preRequestedDrivers?  bookingData.preRequestedDrivers: null,
          requestedDrivers: bookingData.requestedDrivers?  bookingData.requestedDrivers: null,
          // Targeted booking (Find driver, settings.targetedWaitEnabled): the
          // marker and the chosen driver's display copy for the waiting card.
          // This branch builds a whitelist, so they would be dropped otherwise;
          // null (absent) for every other booking.
          targetFlow: bookingData.targetFlow === 1 ? 1 : null,
          targetDriver: bookingData.targetFlow === 1 && bookingData.targetDriver ? bookingData.targetDriver : null,
          driverEstimates: bookingData.driverEstimates?  bookingData.driverEstimates: null,
          streetPickup: bookingData.streetPickup ? bookingData.streetPickup : false,
          fleetadmin: bookingData.fleetadmin? bookingData.fleetadmin:null,
          payment_mode: bookingData.payment_mode,
          booking_from_web: bookingData.booking_from_web? bookingData.booking_from_web: false,
          booking_type_fleetadmin:bookingData.booking_type_fleetadmin,
          booking_type_call_center: bookingData.booking_type_call_center,
          callcenteragent: bookingData.callcenteragent,
          callcentersupervisor: bookingData.callcentersupervisor,
          promo_applied: promoApplied,
          promo_details: promoDetails,
          discount: discountNum,
          // payableAmount was missing, so PaymentDetails fell back to
          // trip_cost and the discount line displayed but didn't reduce
          // the total. Set it explicitly here so the customer sees the
          // discounted amount they actually owe.
          payableAmount: payableAmount,
          ...bookingData.instructionData,
      }
  }
}

// Trip end (REACHED): the address of where the driver stopped, for the
// booking's actual drop. It used to come back from saveAddresses, which also
// read the rider's /users/{uid}/savedAddresses and pushed the place into it;
// that auto-saved list is retired (the rider's Recent places come from their
// own trips, on the phone; named Home / Work / Other places live in the
// top-level /savedAddresses). Only a non-prepaid trip end uses the address
// (addActualsToBooking), so a prepaid one makes no Geocoding call. Never
// throws: a throttled or failed lookup keeps the requested drop's address
// instead of leaving the trip unfinished.
// The lookup has no timeout of its own, and a dead connection would keep the
// trip from ending: past this the requested drop address stands.
const TRIP_END_ADDRESS_MAX_MS = 8000;

export const fetchTripEndAddress = async (booking, driverLocation, settings) => {
  const fallback = (booking.drop && booking.drop.add) || '';
  if (settings && settings.prepaid) return fallback;
  try {
    const address = await Promise.race([
      fetchAddressfromCoords(driverLocation.lat + "," + driverLocation.lng),
      new Promise((resolve) => setTimeout(() => resolve(null), TRIP_END_ADDRESS_MAX_MS)),
    ]);
    return address || fallback;
  } catch (error) {
    console.log('[TRIP END] address lookup failed, keeping the requested drop address:', error);
    return fallback;
  }
};

export const addActualsToBooking = async (booking, address, driverLocation) => {
  const { settingsRef, trackingRef } = firebase;
  const settingsdata = await get(settingsRef);
  const settings = settingsdata.val();
  const end_time = new Date();
  const diff = (end_time.getTime() - parseFloat(booking.startTime)) / 1000;
  const totalTimeTaken = Math.abs(Math.round(diff));
  if(settings.prepaid){ 
    booking.trip_end_time = end_time.getHours() + ":" + end_time.getMinutes() + ":" + end_time.getSeconds();
    booking.endTime = end_time.getTime();
    booking.total_trip_time = totalTimeTaken;
  }else{
    booking.trip_end_time = end_time.getHours() + ":" + end_time.getMinutes() + ":" + end_time.getSeconds();
    booking.endTime = end_time.getTime();
    booking.total_trip_time = totalTimeTaken;
    let cars = store.getState().cartypes.cars;
    let rates = {};
    for (var i = 0; i < cars.length; i++) {
      if (cars[i].name == booking.carType) {
        rates = cars[i];
      }
    }
    const trackingSnap = await get(query(trackingRef(booking.id), orderByKey()));
    const trackingVal = trackingSnap.val();
    const res = await GetTripDistance(trackingVal);
    let distance = settings.convert_to_mile
      ? res.distance / 1.609344
      : res.distance;
    
    // Store original GPS distance for debugging
    const gpsDistance = distance;
    let distanceCalculationMethod = 'GPS_TRACKING';
    
    // CRITICAL FIX: Fallback when GPS tracking fails
    if (distance === 0 || distance < 0.1) {
      console.warn(`GPS tracking returned zero distance for booking ${booking.id}`);
      
      // Option 1: Use estimate distance if available
      if (booking.estimateDistance && booking.estimateDistance > 0) {
        distance = booking.estimateDistance;
        distanceCalculationMethod = 'ESTIMATE_FALLBACK';
        console.log(`Using estimate distance: ${distance} ${settings.convert_to_mile ? 'miles' : 'km'}`);
      } 
      // Option 2: Calculate straight-line distance as minimum
      else if (booking.pickup && driverLocation) {
        const straightDistance = GetDistance(
          booking.pickup.lat, 
          booking.pickup.lng, 
          driverLocation.lat, 
          driverLocation.lng
        );
        // Apply a multiplier for road distance (typically 1.3x straight line)
        distance = straightDistance * (settings.roadDistanceMultiplier || 1.3);
        if (settings.convert_to_mile) {
          distance = distance / 1.609344;
        }
        distanceCalculationMethod = 'STRAIGHT_LINE_FALLBACK';
        console.log(`Using calculated distance: ${distance} ${settings.convert_to_mile ? 'miles' : 'km'}`);
      }
      
      // Ensure minimum distance for any completed trip
      // Use settings value or default to 0.5km/0.3miles
      const defaultMinDistance = settings.convert_to_mile ? 0.3 : 0.5;
      const minDistance = settings.minimumTripDistance || defaultMinDistance;
      
      if (distance < minDistance) {
        distance = minDistance;
        distanceCalculationMethod = 'MINIMUM_DISTANCE';
        console.log(`Applied minimum distance: ${distance} ${settings.convert_to_mile ? 'miles' : 'km'}`);
      }
    }
    const { grandTotal, convenience_fees } = FareCalculator(
      booking,
      distance,
      totalTimeTaken,
      rates,
      null,
      settings.decimal
    );
    booking.drop = {
      add: address,
      lat: driverLocation.lat,
      lng: driverLocation.lng,
    };
    booking.dropAddress = address;
    booking.trip_cost = grandTotal;
    booking.distance = parseFloat(parseFloat(distance).toFixed(settings.decimal));
    booking.convenience_fees = convenience_fees;
    booking.coords = res.coords;
    
    // Store distance calculation metadata for transparency
    booking.distanceCalculationMethod = distanceCalculationMethod;
    booking.gpsTrackedDistance = parseFloat(parseFloat(gpsDistance).toFixed(settings.decimal));
    booking.trackingPointsCount = res.coords ? res.coords.length : 0;

    // Flat-daily driver keeps the whole metered fare. Zero the commission
    // BEFORE the share math below so driver_share lands on grandTotal
    // (fleetCommission is always 0 here — the flat plan excludes fleet
    // drivers by predicate).
    const profile = store.getState().auth.profile;
    const flatActuals = isFlatRateDriver(profile, settings, cars);
    if (flatActuals) {
      booking.convenience_fees = 0;
      booking.flat_rate_driver = true;
    }

    // Flat plan: no fleet cut either — the driver keeps the full fare.
    let fleetCommission_fee = (!flatActuals && booking?.fleetadmin)
    ? parseFloat(((parseFloat(booking?.trip_cost) - parseFloat(booking?.convenience_fees))
       * parseFloat(booking?.fleet_admin_comission) / 100).toFixed(2))
    : 0;
    let driver_fee = parseFloat(parseFloat(parseFloat(booking?.trip_cost) - (parseFloat(booking?.convenience_fees) + parseFloat(fleetCommission_fee) )).toFixed(2));
    booking.fleetCommission = fleetCommission_fee ? booking.offerFare > 0 && booking.offerFare < grandTotal ? "0" : fleetCommission_fee : "0";
    booking.driver_share = driver_fee? driver_fee : 0;
    
  }
  return booking;
};

export const updateDriverQueue = (booking) => {
  return booking;
};

export const driverQueue= false;